import { useCallback } from 'react';
import { axiosInstance } from '@/api/axiosInstance';
import { useRequestContext } from '@/hooks/useRequestContext';
import { useBaseService } from '@/services/useBaseService';
import {
  calculateDeliveryDate,
  calculatePlanningDates,
  deleteOrderProduct,
  getOrder,
  listOrderProducts,
  listOrders,
  saveOrder,
  saveOrderProducts,
  type GetOrderResult,
  type ListOrdersParams,
  type ListOrdersResult,
  type SaveOrderResult,
} from '@/services/orders/orderService';

export const useOrderService = () => {
  const { source, userId } = useRequestContext();

  const listOrdersService = useCallback(
    async (params: ListOrdersParams): Promise<ListOrdersResult> => {
      return listOrders(axiosInstance, params, source);
    },
    [source],
  );

  const { data, error, loading, execute, reset } = useBaseService(listOrdersService);

  const getOrderService = useCallback(
    async (orderNumber: string): Promise<GetOrderResult> => {
      return getOrder(axiosInstance, { orderNumber }, source, userId);
    },
    [source, userId],
  );

  const getOrderProductsService = useCallback(
    async (orderNumber: string) => {
      return listOrderProducts(axiosInstance, { orderNumber }, source, userId);
    },
    [source, userId],
  );

  const saveOrderService = useCallback(
    async (order: Parameters<typeof saveOrder>[1]['order']): Promise<SaveOrderResult> => {
      return saveOrder(axiosInstance, { order }, source);
    },
    [source],
  );

  const saveOrderProductsService = useCallback(
    async (
      orderNumber: string,
      order: Parameters<typeof saveOrder>[1]['order'],
      products: Parameters<typeof saveOrderProducts>[1]['products'],
    ) => {
      return saveOrderProducts(axiosInstance, { orderNumber, order, products }, source);
    },
    [source],
  );

  const deleteOrderProductService = useCallback(
    async (orderNumber: string, itemNumber: string) => {
      return deleteOrderProduct(axiosInstance, { orderNumber, itemNumber }, source);
    },
    [source],
  );

  const calculatePlanningDatesService = useCallback(
    async (routeCode: string, requestedDeliveryDate: Date) => {
      return calculatePlanningDates(axiosInstance, { routeCode, requestedDeliveryDate }, source);
    },
    [source],
  );

  const calculateDeliveryDateService = useCallback(
    async (routeCode: string, plannedTransportationDate: Date) => {
      return calculateDeliveryDate(axiosInstance, { routeCode, plannedTransportationDate }, source);
    },
    [source],
  );

  return {
    orders: data?.data ?? [],
    total: data?.count ?? 0,
    error,
    loading,
    loadOrders: execute,
    loadOrderByNumber: getOrderService,
    loadOrderProductsByNumber: getOrderProductsService,
    calculateDeliveryDate: calculateDeliveryDateService,
    calculatePlanningDates: calculatePlanningDatesService,
    deleteOrderProduct: deleteOrderProductService,
    saveOrder: saveOrderService,
    saveOrderProducts: saveOrderProductsService,
    reset,
  };
};
